import { useQueries } from "@tanstack/react-query";
import { metricsApi } from "../api/metrics";
import { qk } from "../../lib/queryKeys";
import type { MetricsPatternsParams } from "../api/types";
import type { ComparisonGroup } from "./useComparisonGroups";

/**
 * Fetches a time-of-day / weekly pattern for each comparison group in parallel
 * using the existing patterns endpoint (one request per group). Used to overlay
 * one line per group on a single pattern chart. Reuses the same query keys as
 * useMetricsPatterns so requests dedupe/cache with the rest of the app.
 */
export const useComparisonPatterns = (
  groups: ComparisonGroup[],
  metric: string,
  granularity: MetricsPatternsParams["granularity"]
) => {
  const results = useQueries({
    queries: groups.map((group) => {
      const params: MetricsPatternsParams = {
        ...group.filterParams,
        metric,
        granularity,
      };
      return {
        queryKey: qk.metrics.patterns(metric, params),
        queryFn: () => metricsApi.getPatterns(params),
        staleTime: 5 * 60 * 1000,
      };
    }),
  });

  return groups.map((group, index) => ({
    group,
    data: results[index]?.data,
    isLoading: results[index]?.isLoading ?? false,
    isError: results[index]?.isError ?? false,
  }));
};
